import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlined";
import { Box, Card, Chip, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import type { UnderutilizedNurse } from "../hooks/useHiringData";
import { COLORS, ROLE_GROUP_MATCHES, type RoleGroup, type RoleRecommendation, type ShiftFilter } from "../types";
import { NewHireCard } from "./NewHireCard";
import { UnderutilizedRoleCard } from "./UnderutilizedRoleCard";

interface HiringAIRecommendProps {
	recommendations: RoleRecommendation[];
	loading: boolean;
	underutilizedNurses: UnderutilizedNurse[];
	onAcceptExisting: (role: string, filter: ShiftFilter, nurses: UnderutilizedNurse[]) => void;
	onAcceptHire: (role: string, count: number, filter: ShiftFilter) => void;
}

export const HiringAIRecommend: FC<HiringAIRecommendProps> = ({ recommendations, loading, underutilizedNurses, onAcceptExisting, onAcceptHire }) => {
	const totalShifts = recommendations.reduce((sum, rec) => sum + rec.total, 0);

	const getUnderutilizedForRole = (role: string): UnderutilizedNurse[] => {
		const roleMatches = ROLE_GROUP_MATCHES[role as RoleGroup] ?? [];
		return underutilizedNurses.filter((nurse) => roleMatches.some((r: string) => nurse.nurse.role.includes(r)));
	};

	if (loading) {
		return (
			<Card
				sx={{
					p: 3,
					borderLeft: `4px solid ${COLORS.primary}`,
					boxShadow: (theme) => `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
					borderRadius: "12px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 2,
					minHeight: 160,
				}}
			>
				<CircularProgress size={24} sx={{ color: COLORS.primary }} />
				<Typography sx={{ fontSize: "15px", fontWeight: 600, color: COLORS.primary }}>Analyzing staffing gaps...</Typography>
			</Card>
		);
	}

	return (
		<Card
			data-testid="hiring-ai-recommend-card"
			sx={{
				p: 3,
				borderLeft: `4px solid ${COLORS.primary}`,
				boxShadow: (theme) => `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
				borderRadius: "12px",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
				<AutoAwesomeIcon sx={{ color: COLORS.primary, fontSize: 22 }} />
				<Typography sx={{ fontSize: "18px", fontWeight: 700, color: "primary.dark" }}>AI Recommendations</Typography>
			</Box>
			<Typography sx={{ fontSize: "14px", color: COLORS.primary, mb: 2.5 }}>
				Suggested actions to achieve full coverage &bull; {totalShifts} total shifts needed
			</Typography>

			<Box sx={{ mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
					<PersonAddAltIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
					<Typography sx={{ fontSize: "15px", fontWeight: 700, color: "primary.dark" }}>New Hires Needed</Typography>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					{recommendations
						.filter((rec) => rec.total > 0)
						.map((recommendation) => (
							<NewHireCard key={`hire-${recommendation.role}`} recommendation={recommendation} onAccept={onAcceptHire} />
						))}

					{recommendations.every((rec) => rec.total === 0) && (
						<Box sx={{ textAlign: "center", py: 2 }}>
							<Typography sx={{ fontSize: "14px", color: COLORS.success, fontWeight: 600 }}>Fully staffed — no additional hires needed</Typography>
						</Box>
					)}
				</Box>
			</Box>
			{underutilizedNurses.length > 0 && (
				<Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
						<PersonOutlineIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
						<Typography sx={{ fontSize: "15px", fontWeight: 700, color: "primary.dark" }}>Underutilized Staff</Typography>
						<Chip
							label={`${underutilizedNurses.length} nurses below capacity`}
							size="small"
							sx={{ bgcolor: "#FFF3E0", color: COLORS.warning, fontWeight: 600, fontSize: "12px" }}
						/>
					</Box>

					{recommendations.map((recommendation) => {
						const roleNurses = getUnderutilizedForRole(recommendation.role);
						if (roleNurses.length === 0) return null;
						return (
							<UnderutilizedRoleCard
								key={`existing-${recommendation.role}`}
								role={recommendation.role}
								underutilizedNurses={roleNurses}
								onAccept={onAcceptExisting}
							/>
						);
					})}
				</Box>
			)}
		</Card>
	);
};
